import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BookOpen, Activity, BarChart3, ArrowRight, CheckCircle,
  AlertCircle, Clock, Zap, Shield, ChevronRight, ExternalLink,
  Code2, Globe, RefreshCw
} from 'lucide-react';

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Service status dot ────────────────────────────────────────────────────────
const StatusDot = ({ status }) => {
  const map = {
    operational: 'bg-emerald-500',
    degraded:    'bg-amber-400',
    outage:      'bg-red-500',
    unknown:     'bg-slate-300',
  };
  return <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${map[status] || map.unknown}`} />;
};

const StatusPill = ({ status }) => {
  const map = {
    operational: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    degraded:    'bg-amber-50 text-amber-700 border-amber-200',
    outage:      'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${map[status] || 'bg-slate-50 text-slate-500 border-slate-200'}`}>
      <StatusDot status={status} />
      {status === 'operational' ? 'All systems operational' : status === 'degraded' ? 'Partial degradation' : 'Service disruption'}
    </span>
  );
};

// ─── Quick-start card ─────────────────────────────────────────────────────────
const Card = ({ icon: Icon, title, description, href, external, badge }) => {
  const inner = (
    <div className="group flex flex-col h-full p-5 bg-white border border-slate-200 rounded-2xl hover:border-blue-300 hover:shadow-md transition-all cursor-pointer">
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center group-hover:bg-blue-100 transition-colors">
          <Icon className="w-5 h-5 text-blue-600" />
        </div>
        {badge && (
          <span className="px-2 py-0.5 text-[10px] font-bold bg-blue-100 text-blue-600 rounded-full">{badge}</span>
        )}
      </div>
      <h3 className="font-semibold text-slate-800 text-sm mb-1.5">{title}</h3>
      <p className="text-xs text-slate-500 leading-relaxed flex-1">{description}</p>
      <div className="flex items-center gap-1 mt-3 text-xs font-semibold text-blue-600 group-hover:gap-2 transition-all">
        {external ? 'Open' : 'Explore'} <ArrowRight className="w-3 h-3" />
      </div>
    </div>
  );
  return external
    ? <a href={href} target="_blank" rel="noopener noreferrer">{inner}</a>
    : <Link to={href}>{inner}</Link>;
};

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function DeveloperPortal() {
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);

  useEffect(() => {
    fetch(`${BASE_URL}/api/health`)
      .then(r => r.json())
      .then(d => setHealth(d))
      .catch(() => setHealth({ status: 'unknown', services: {} }))
      .finally(() => setHealthLoading(false));
  }, []);

  const overallStatus = health?.status || 'unknown';

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>

      {/* ── Portal header ──────────────────────────────────────────────────── */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white text-xs font-black">N</span>
              </div>
            </Link>
            <span className="text-slate-300">/</span>
            <span className="text-sm font-semibold text-slate-700">api.nanokart.ai</span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/partners/login"
              className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors flex items-center gap-1.5"
            >
              Get API Key <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12 space-y-12">

        {/* ── Hero ───────────────────────────────────────────────────────── */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-100 rounded-full text-xs font-semibold text-blue-700 mb-2">
            <Globe className="w-3.5 h-3.5" />
            api.nanokart.ai
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight">
            Nanokart Developer Portal
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">
            Everything you need to integrate Nanokart's AI APIs into your store. Virtual Try-On, Moments, Size Recommendation, and SmartGate  -  all in one place.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap pt-2">
            <button
              onClick={() => navigate('/docs')}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-colors"
              data-testid="view-api-reference-btn"
            >
              <BookOpen className="w-4 h-4" /> View API Reference
            </button>
          </div>
        </div>

        {/* ── Platform status widget ──────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="platform-status-widget">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Activity className="w-4 h-4 text-slate-500" />
              <h2 className="font-semibold text-slate-800">Platform Status</h2>
            </div>
            <div className="flex items-center gap-3">
              {healthLoading
                ? <span className="text-xs text-slate-400 flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" /> Checking...</span>
                : <StatusPill status={overallStatus} />
              }
              <a
                href={`${BASE_URL}/api/health`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-slate-400 hover:text-blue-600 transition-colors flex items-center gap-1"
              >
                <Code2 className="w-3 h-3" /> Raw JSON
              </a>
            </div>
          </div>

          <div className="px-6 py-5">
            {healthLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {Object.entries(health?.services || {}).map(([key, svc]) => (
                  <div key={key} className="flex flex-col items-center gap-2 p-3 bg-slate-50 rounded-xl border border-slate-100">
                    <StatusDot status={svc.status} />
                    <span className="text-xs font-medium text-slate-600 text-center leading-tight">{svc.label}</span>
                    <span className={`text-[10px] font-semibold capitalize ${svc.status === 'operational' ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {svc.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {health?.timestamp && (
              <p className="text-xs text-slate-400 mt-4 text-right">
                Last checked: {new Date(health.timestamp).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                {health.region && <span className="ml-2 text-slate-300">· {health.region}</span>}
              </p>
            )}
          </div>
        </div>

        {/* ── Quick access cards ──────────────────────────────────────────── */}
        <div>
          <h2 className="text-lg font-bold text-slate-800 mb-4">Developer Resources</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card
              icon={BookOpen}
              title="API Reference"
              description="Complete documentation for all 27 endpoints  -  Virtual Try-On, Moments, Size Recommendation, and SmartGate. With cURL, Python and JavaScript examples."
              href="/docs"
            />
            <Card
              icon={Zap}
              title="Virtual Try-On"
              description="Let your shoppers try any garment before they buy. Pass a garment URL and model image  -  get back a photorealistic try-on in under 4 seconds."
              href="/docs#tryon-from-url"
            />
            <Card
              icon={Shield}
              title="SmartGate"
              description="Partner-configurable customer wallet. Welcome bonus, purchase rewards, per-action point costs, and Shopify auto-credit via webhook."
              href="/docs#sg-get-settings"
              badge="PRO"
            />
            <Card
              icon={Activity}
              title="Health Check API"
              description="Real-time platform status with per-service granularity. No authentication required  -  embed in your own dashboards or monitoring tools."
              href="/docs#health-check"
            />
            <Card
              icon={BarChart3}
              title="Partner Status API"
              description="Full account snapshot: subscription plan, usage balance, SmartGate state, API key count, and team size. One call to verify account health."
              href="/docs#partner-status"
            />
            <Card
              icon={BookOpen}
              title="Usage Dashboard"
              description="Monitor API consumption, usage balance, and call history from your partner dashboard."
              href="/partners/wallet"
            />
          </div>
        </div>

        {/* ── Base URL callout ──────────────────────────────────────────────── */}
        <div className="bg-slate-900 rounded-2xl p-6 text-white">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">Base URL</p>
              <p className="font-mono text-xl font-bold text-white">https://api.nanokart.ai</p>
              <p className="text-slate-400 text-sm mt-2">All endpoints are prefixed with <code className="bg-slate-800 px-1.5 py-0.5 rounded text-slate-200">/api</code></p>
            </div>
            <div className="flex flex-col gap-2 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span className="text-slate-300">HTTPS only</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span className="text-slate-300">JSON request & response</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span className="text-slate-300">X-API-Key + X-Partner-Id auth</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span className="text-slate-300">ap-south-1 region (Mumbai)</span>
              </div>
            </div>
          </div>
          <div className="mt-5 pt-5 border-t border-slate-800 grid sm:grid-cols-3 gap-4 text-center">
            {[
              { label: 'API Version', value: 'v2.0.0' },
              { label: 'Avg TryOn Latency', value: '~3.5s' },
              { label: 'Uptime SLA', value: '99.9%' },
            ].map(({ label, value }) => (
              <div key={label}>
                <p className="text-2xl font-black text-white">{value}</p>
                <p className="text-xs text-slate-500 mt-1">{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Footer ────────────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center justify-between gap-4 pb-6 text-xs text-slate-400 border-t border-slate-200 pt-6">
          <span>© 2026 Nanokart.ai · api.nanokart.ai · v2.0.0</span>
          <div className="flex items-center gap-4">
            <Link to="/docs" className="hover:text-blue-600 transition-colors">API Reference</Link>
            <Link to="/pricing" className="hover:text-blue-600 transition-colors">Pricing</Link>
            <Link to="/contact" className="hover:text-blue-600 transition-colors">Support</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
